import { useCallback, useEffect, useMemo } from "react";

import {
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getGroupedRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useAtom } from "jotai";

import { ScrollArea, ScrollBar } from "src/components/ui/scroll-area";
import { Skeleton } from "src/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { cn } from "src/lib/utils";

/**
 * This component renders a common data table
 * using Jotai for state management and TanStack Table for rendering.
 *
 * @param atom - Jotai atom that holds the table state and data
 * @param data - array of data to be displayed in the table
 * @param idField - name of the field that uniquely identifies each row
 * @param columns - array of column definitions for the table
 * @param initialState - initial state for the table, such as sorting, filtering, etc.
 * @param loadingData - optional loading data to display when the table is loading
 * @param className - additional CSS classes to apply to the table container
 * @param onRowClick - optional callback invoked when a row is clicked
 * @return {Element} A React element representing the data table
 */
export function DataTable({
  atom,
  data,
  idField = "id",
  columns,
  initialState,
  loadingData: _loadingData,
  className,
  headerClassName,
  alternateRowColors = false,
  onRowClick,
}) {
  const [{ tableState, loading }, setStore] = useAtom(atom);

  const setTableState = useCallback(
    (newState, field) => {
      setStore((prev) => {
        if (newState instanceof Function) {
          // handle functional updates
          newState = newState(prev.tableState[field] || []);
        }

        return {
          ...prev,
          tableState: {
            ...prev.tableState,
            [field]: newState,
          },
        };
      });
    },
    [setStore]
  );

  useEffect(() => {
    if (initialState && !tableState) {
      setStore((prev) => ({
        ...prev,
        tableState: {
          ...prev.tableState,
          ...initialState,
        },
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialState, tableState]);

  // Set initial grouping state to group by the columns that have enableGrouping set to true
  useEffect(() => {
    const groupingColumns = columns
      .filter((col) => col.enableGrouping)
      .map((col) => col.accessorKey || col.id);
    setTableState(groupingColumns, "grouping");
  }, [columns, idField, setTableState]);

  // Memoize the data to avoid unnecessary re-renders
  const finalData = useMemo(() => {
    if (!data?.length) {
      // Generate loading data for the skeleton state
      const loadingData =
        _loadingData ||
        Array.from({ length: 14 }, () => ({
          [idField]: Math.random().toString().substring(2, 11),
        }));
      return loading ? loadingData : [];
    }

    return data;
  }, [data, _loadingData, loading, idField]);

  const table = useReactTable({
    data: finalData,
    columns,
    state: tableState || {},
    getRowId: (row) => row[idField],
    getSubRows: (row) => row.subRows,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getGroupedRowModel: getGroupedRowModel(),
    onSortingChange: (v) => setTableState(v, "sorting"),
    onColumnFiltersChange: (v) => setTableState(v, "columnFilters"),
    onExpandedChange: (v) => {
      // check if empty object was passed, which happens on initial load.
      // We don't want to set the state in that case, initialState is already set in the useEffect above
      if (Object.keys(v).length === 0 && v.constructor === Object) {
        return;
      }

      setTableState(v, "expanded");
    },
    onColumnVisibilityChange: (v) => setTableState(v, "columnVisibility"),
    meta: { loading },
  });

  return (
    <div
      className={cn(
        "flex h-full max-h-[calc(100vh-200px)] flex-col",
        className
      )}
    >
      <ScrollArea className="-mr-3 -mb-3 pr-3 pb-3" type="always">
        <Table
          className="group border-separate border-spacing-0"
          data-loading={loading ? "true" : "false"}
        >
          <TableHeader className="sticky top-0 z-20 [&>tr:first-child>th]:!border-none">
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow
                key={headerGroup.id}
                className={cn(
                  "bg-background hover:bg-background",
                  headerClassName
                )}
              >
                {headerGroup.headers.map((header) => {
                  const { meta = {} } = header.column.columnDef;

                  return (
                    <TableHead
                      key={header.id}
                      colSpan={header.colSpan}
                      className={cn(
                        "text-foreground h-8 whitespace-nowrap",
                        meta.headerCellAlign === "left" && "text-left",
                        meta.headerCellAlign === "right" && "text-right",
                        (!meta.headerCellAlign ||
                          meta.headerCellAlign === "center") &&
                          "text-center",
                        meta.rightBorder && "border-border border-r",
                        meta.leftBorder && "border-border border-l",
                        meta.sticky && "bg-background left-0 z-20 sm:sticky",
                        meta.className
                      )}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>

          <TableBody
            className={cn(
              "[&_tr:first-child>td:first-child]:!rounded-tl-lg [&_tr:first-child>td:last-child]:!rounded-tr-lg [&_tr:last-child>td:first-child]:!rounded-bl-lg [&_tr:last-child>td:last-child]:!rounded-br-lg",
              "[&_tr:first-child>td]:border-t [&_tr>td:first-child]:border-l [&_tr>td:last-child]:border-r"
            )}
          >
            {table.getRowModel().rows.map((row, index) => {
              // row metadata is saved in the original data
              const {
                highlight,
                noHighlight,
                className: rowClassName,
              } = row.original?.meta || {};
              const isRowClickable = !!onRowClick || row.getCanExpand();

              return (
                <TableRow
                  key={row.id}
                  onClick={
                    isRowClickable
                      ? () => {
                          if (loading) return;
                          if (onRowClick) {
                            onRowClick(row);
                          }
                          if (row.getCanExpand()) {
                            row.toggleExpanded();
                          }
                        }
                      : undefined
                  }
                  className={cn(
                    !loading && "hover:[&>td]:bg-accent",
                    isRowClickable &&
                      "hover:[&>td]:bg-muted cursor-pointer font-medium"
                  )}
                >
                  {row.getVisibleCells().map((cell) => {
                    const { meta = {} } = cell.column.columnDef;

                    return (
                      <TableCell
                        key={cell.id}
                        className={cn(
                          "text-foreground border-border h-11 overflow-hidden border-b whitespace-nowrap tabular-nums",
                          meta.rightBorder && "border-border border-r",
                          meta.leftBorder && "border-border border-l",
                          meta.sticky && "left-0 z-10 sm:sticky",
                          rowClassName,
                          row.getCanExpand() && !noHighlight
                            ? "bg-secondary"
                            : alternateRowColors && index % 2 === 0
                              ? "bg-muted"
                              : "bg-background",
                          meta.className
                        )}
                        style={{
                          backgroundColor: highlight
                            ? "color-mix(in oklab, var(--primary), var(--background) 75%)"
                            : undefined,
                        }}
                      >
                        {loading && !meta.noSkeleton ? (
                          <Skeleton
                            className={cn(
                              "size-full text-transparent [&_*]:opacity-0",
                              "group-data-[loading=true]:bg-muted/60"
                            )}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </Skeleton>
                        ) : (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <ScrollBar orientation="horizontal" />
      </ScrollArea>
    </div>
  );
}
